import { useEffect, useRef, useState, type FormEvent } from "react";
import { ModalShell } from "../ModalShell";
import { useOpenRouterKey } from "../../hooks/useOpenRouterKey";
import dialogStyles from "../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./UpdateOpenRouterKeyModal.module.css";

type UpdateOpenRouterKeyModalProps = {
  tenantId: string;
  instanceName: string;
  onClose: () => void;
  // Fired once the key is applied (gateway restart kicked off) so the parent
  // can refresh the credits cell. The modal then shows its own restart hint
  // rather than closing, so it does not also pop a toast.
  onApplied: () => void;
};

type SubmitPhase = "idle" | "validating" | "applying";

const APPLYING_COPY_DELAY_MS = 2500;

const formatUpdatedAt = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

// Self-serve OpenRouter key rotation. The backend validates the key against
// OpenRouter before storing + pushing it, so submitting a bad key can never
// break a working instance.
export const UpdateOpenRouterKeyModal = ({
  tenantId,
  instanceName,
  onClose,
  onApplied,
}: UpdateOpenRouterKeyModalProps): JSX.Element => {
  const { status, keyStatus, setKey } = useOpenRouterKey(tenantId);

  const [keyInput, setKeyInput] = useState<string>("");
  const [phase, setPhase] = useState<SubmitPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  // Non-null once the key is applied: switches the body to the restart hint.
  const [applied, setApplied] = useState<{ limitUsd: number | null } | null>(null);
  const phaseTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (phaseTimer.current !== null) window.clearTimeout(phaseTimer.current);
    },
    [],
  );

  const trimmed = keyInput.trim();
  const busy = phase !== "idle";
  const looksWrongPrefix = trimmed.length > 0 && !trimmed.startsWith("sk-or-");
  const titleId = "openrouter-key-dialog-title";

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (trimmed.length === 0 || busy) return;
    setError(null);
    setPhase("validating");
    // One PUT does validate + apply; the phase flip is purely copy so the
    // ~30s instance update doesn't look stuck on "verifying".
    phaseTimer.current = window.setTimeout(() => setPhase("applying"), APPLYING_COPY_DELAY_MS);

    const result = await setKey(trimmed);

    if (phaseTimer.current !== null) window.clearTimeout(phaseTimer.current);
    setPhase("idle");
    if (result.ok) {
      onApplied();
      // Don't close: the key push restarts the gateway, and opening the
      // instance during that ~15s window throws "couldn't reach your
      // instance". Show the restart hint and let the user dismiss it.
      setApplied({ limitUsd: result.value.limitUsd ?? null });
      return;
    }
    setError(result.message);
  };

  const submitLabel =
    phase === "validating"
      ? "Verifying key with OpenRouter…"
      : phase === "applying"
        ? "Updating your instance — takes ~30 seconds"
        : "Verify & apply key";

  if (applied !== null) {
    return (
      <ModalShell onClose={onClose} titleId={titleId} size="sm">
        <header className={dialogStyles.dialogHeader}>
          <h3 className={dialogStyles.dialogTitle} id={titleId}>
            Key updated
          </h3>
          <button
            type="button"
            className={dialogStyles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div data-testid="openrouter-key-success">
          <p className={styles.successTitle}>
            OpenRouter key applied for <strong>{instanceName}</strong>
            {applied.limitUsd != null ? ` — $${applied.limitUsd.toFixed(2)} limit` : ""}.
          </p>
          <p className={styles.restartHint}>
            Your instance is restarting to load the new key. Give it about 15 seconds
            before you open it — opening it right away may show a "couldn't reach your
            instance" message.
          </p>
          <footer className={dialogStyles.footer}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onClose}
              data-testid="openrouter-key-done"
              autoFocus
            >
              Done
            </button>
          </footer>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={busy ? () => undefined : onClose} titleId={titleId} size="sm">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>
          Update OpenRouter API key
        </h3>
        <button
          type="button"
          className={dialogStyles.closeBtn}
          onClick={onClose}
          aria-label="Close"
          disabled={busy}
        >
          ×
        </button>
      </header>

      <p className={styles.explainer}>
        <strong>{instanceName}</strong> runs on your own OpenRouter account. Paste a new
        OpenRouter API key below — keys from other providers won't work. You can create one
        at{" "}
        <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noreferrer">
          openrouter.ai/settings/keys
        </a>
        .
      </p>

      <p className={styles.statusLine} data-testid="openrouter-key-status">
        {status === "loading" && "Checking current key…"}
        {status === "error" && "Couldn't check the current key."}
        {status === "ready" &&
          (keyStatus?.configured === true
            ? `Key configured — last updated ${
                keyStatus.updatedAt !== null && keyStatus.updatedAt !== undefined
                  ? formatUpdatedAt(keyStatus.updatedAt)
                  : "unknown"
              }.`
            : "No key on file yet.")}
      </p>

      <form onSubmit={(e) => void handleSubmit(e)}>
        <label className={dialogStyles.field}>
          <span>New OpenRouter API key</span>
          <input
            type="text"
            className={styles.keyInput}
            style={{ WebkitTextSecurity: "disc" } as React.CSSProperties}
            value={keyInput}
            onChange={(e) => {
              setKeyInput(e.target.value);
              if (error) setError(null);
            }}
            placeholder="sk-or-v1-…"
            autoFocus
            disabled={busy}
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-testid="openrouter-key-input"
          />
        </label>

        {looksWrongPrefix && (
          <p className={styles.prefixWarning}>
            This doesn't look like an OpenRouter key — they start with{" "}
            <code>sk-or-</code>. You can still try it.
          </p>
        )}

        <p className={dialogStyles.hint}>
          We verify the key with OpenRouter first, then update your instance. Your current
          key keeps working until the new one is verified.
        </p>

        {error && (
          <p className={dialogStyles.errorBanner} data-testid="openrouter-key-error">
            {error}
          </p>
        )}

        <footer className={dialogStyles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={busy || trimmed.length === 0}
            data-testid="openrouter-key-submit"
          >
            {submitLabel}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};
